import { LinkSize, LinkVariant } from '../../../common/components/Link/contracts';
import { isBefore, isEqual, parseISO } from 'date-fns';
import { MatchPeriodFilter, MatchSort, MatchStatus, MatchStatusFilter, Club } from '@repo/types';
import { ClubNameType } from '@ui/fixtures/contracts';
import FixtureListUI from './FixtureListUI';
import { SkeletonLoader } from '@ui/common/components/SkeletonLoader';
import { useMatchesQuery } from '@data/matches/useMatchesQuery';
import { useMemo } from 'react';

// @ts-ignore fix vite import type
const league = import.meta.env.VITE_FWP_LEAGUE;

export type FixtureListProps = {
  groupByDate: boolean;
  clubFromSlug?: boolean;
  className?: string;
  period?: MatchPeriodFilter;
  periodLimit?: number;
  status?: MatchStatusFilter;
  limit?: number;
  sort?: MatchSort;
  from?: string;
  to?: string;
  competitionId?: string;
  teamId?: string;
  nameType?: ClubNameType;
  style?: React.CSSProperties;
  columns?: number;
  linkLabel?: string;
  linkUrl?: string;
  linkTargetBlank?: boolean;
  linkVariant?: LinkVariant;
  linkSize?: LinkSize;
  title?: string;
  extraData?: {
    club?: Club;
  };
};

export function FixtureList({
  groupByDate,
  clubFromSlug,
  extraData,
  className,
  period,
  periodLimit,
  limit,
  sort,
  status,
  from,
  to,
  competitionId,
  teamId,
  nameType,
  style,
  columns,
  linkLabel,
  linkUrl,
  linkTargetBlank,
  linkVariant = LinkVariant.Outlined,
  linkSize = LinkSize.m,
  title,
}: FixtureListProps) {
  const firstTeam = extraData?.club?.teams?.find((team) => team.isDefault);
  const { data: fixtures, isLoading } = useMatchesQuery({
    league,
    competition: competitionId ?? '',
    teamId: clubFromSlug && firstTeam ? firstTeam.id : teamId ?? '',
    period,
    periodLimit,
    startDate: period === MatchPeriodFilter.Custom && from ? from : '',
    endDate: period === MatchPeriodFilter.Custom && to ? to : '',
    limit,
    sort,
    status,
  });

  const matchesWithoutPostponed = useMemo(() => {
    const today = new Date();
    return (
      fixtures?.items.filter((match) => {
        if (match.status !== MatchStatus.Postponed) {
          return true;
        }

        const matchDate = parseISO(match.date);

        if (isBefore(today, matchDate)) {
          return true;
        } else if (isEqual(today, matchDate)) {
          return true;
        } else {
          return false;
        }
      }) ?? []
    );
  }, [fixtures?.items]);

  if (isLoading) {
    return (
      <div style={{ display: 'block', width: '100%', height: '100%' }}>
        <SkeletonLoader count={21} />
      </div>
    );
  }

  if (!fixtures || fixtures.items.length === 0) {
    return null;
  }

  return (
    <FixtureListUI
      fixtures={matchesWithoutPostponed}
      groupByDate={groupByDate}
      nameType={nameType}
      columns={columns}
      className={className}
      style={style}
      linkLabel={linkLabel}
      linkUrl={linkUrl}
      linkTargetBlank={linkTargetBlank}
      linkVariant={linkVariant}
      linkSize={linkSize}
      title={title}
      sortByDate={sort ? (sort?.toUpperCase() as 'ASC' | 'DESC') : 'DESC'}
    />
  );
}
